import { api } from './api';

const THROTTLE_SEC = 5; // server expects ≤ 1 progress write per 5s per material
// % of scrollable distance that counts as "fully read" — when user reaches
// this point, we send pct=100 to the server. Most readers don't bother
// scrolling all the way to the bottom border, so anchoring slightly above
// the end is more realistic than requiring 100%.
const COMPLETE_AT_SCROLL_PCT = 90;

/**
 * Tracks how much of a text material the user has read via scroll-depth on
 * the page's outer scroll container. Sends progress to the same endpoint as
 * video/audio (`POST /me/playback/:id/progress`) by reusing the `sec/duration`
 * pair as `scrollTop / (scrollableHeight * 0.9)` so that hitting 90% scroll
 * maps to pct=100 in the DB.
 *
 * Short text (fits entirely without scrolling) gets pct=100 immediately on
 * attach, since the user has already seen everything.
 *
 * `contentEl` — сама стаття всередині контейнера. Передавати ОБОВ'ЯЗКОВО, коли
 * під статтею живе ще щось (блок коментарів): інакше вимірювання бере
 * scrollHeight усього контейнера, куди входять і коментарі, і людина отримує
 * «прочитано» щойно догортає до чужих реплік. Полагодити це заднім числом
 * неможливо — сервер пише maxProgressPct через Math.max, і завищений відсоток
 * назад не відкочується.
 *
 * Returns a teardown callback.
 */
export function attachTextProgressTracking(
  scrollEl: HTMLElement,
  materialId: string,
  contentEl?: HTMLElement,
): () => void {
  let lastSent = -Infinity;
  let disposed = false;
  // Trailing-edge timer. Без нього перший scroll випалює одну точку (часто
  // scrollTop≈1px → 1%) і далі throttle глушить усе до наступного скролу.
  // Коли користувач долітає до низу і зупиняється — нових scroll-подій нема,
  // тож кінцева позиція ніколи не доходила до сервера. Тримаємо тут таймер,
  // який перепризначається на кожен скрол: він пошле _останню_ позицію
  // через THROTTLE_SEC після останньої події.
  let trailingTimer: ReturnType<typeof setTimeout> | null = null;

  const send = (sec: number, duration: number): void => {
    api
      .postProgress(materialId, { sec, duration })
      .catch(() => {
        /* silent — best-effort tracking */
      });
  };

  /**
   * Скільки треба прокрутити, щоб побачити статтю до кінця.
   *
   * Рахуємо через getBoundingClientRect, а не offsetTop: .scroll не має
   * position: relative, тож offsetTop контенту вимірювався б відносно зовсім
   * іншого предка — класична тиха поломка, яка дала б стабільно неправильний,
   * але правдоподібний масштаб.
   */
  function measureMaxScrollable(): number {
    if (!contentEl) return scrollEl.scrollHeight - scrollEl.clientHeight;
    const content = contentEl.getBoundingClientRect();
    const container = scrollEl.getBoundingClientRect();
    const contentBottom = content.bottom - container.top + scrollEl.scrollTop;
    return contentBottom - scrollEl.clientHeight;
  }

  // Scale duration so scrollTop reaches it at COMPLETE_AT_SCROLL_PCT of max.
  // Anything past that point is clamped to 100 by the server's Math.min(100, ...).
  let scaledDuration = 1;
  let fitsWithoutScrolling = false;

  function remeasure(): void {
    const maxScrollable = measureMaxScrollable();
    // Content fits without scrolling → user has read everything visible.
    // Перевіряємо на кожному перемірі, а не лише на старті: коротка стаття
    // «виростає», коли всередині неї довантажуються картинки.
    fitsWithoutScrolling = maxScrollable <= 0;
    scaledDuration = Math.max(1, Math.floor((maxScrollable * COMPLETE_AT_SCROLL_PCT) / 100));
  }
  remeasure();

  if (fitsWithoutScrolling) {
    send(1, 1); // pct = round(1/1 * 100) = 100
  }

  const flush = (): void => {
    if (disposed) return;
    trailingTimer = null;
    lastSent = Date.now() / 1000;
    send(Math.floor(scrollEl.scrollTop), scaledDuration);
  };

  const onScroll = (): void => {
    if (disposed) return;
    const nowSec = Date.now() / 1000;
    const elapsed = nowSec - lastSent;
    if (elapsed >= THROTTLE_SEC) {
      // Leading edge — пошли одразу.
      if (trailingTimer) {
        clearTimeout(trailingTimer);
        trailingTimer = null;
      }
      flush();
    } else {
      // Перепризначаємо trailing-таймер: остання позиція виграє.
      if (trailingTimer) clearTimeout(trailingTimer);
      trailingTimer = setTimeout(flush, (THROTTLE_SEC - elapsed) * 1000);
    }
  };

  scrollEl.addEventListener('scroll', onScroll, { passive: true });

  // Спостерігаємо саме СТАТТЮ, а не контейнер. Контейнер міняє висоту ще й тоді,
  // коли в Telegram WebView відкривається клавіатура (фокус у полі коментаря) —
  // це не привід переоцінювати прочитане.
  const observer =
    contentEl && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          if (!disposed) remeasure();
        })
      : null;
  observer?.observe(contentEl!);

  return () => {
    disposed = true;
    observer?.disconnect();
    scrollEl.removeEventListener('scroll', onScroll);
    if (trailingTimer) {
      clearTimeout(trailingTimer);
      trailingTimer = null;
    }
  };
}
